var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { createAsyncThunk, nanoid } from '@reduxjs/toolkit';
import { initializeApp } from 'firebase/app';
import { fetchAndActivate, getAll, getRemoteConfig, } from 'firebase/remote-config';
import WebFont from 'webfontloader';
import { EditorActions } from 'app/stores/editor/actions';
import { hasIndexedDB } from 'app/utils/caniuse';
import { errorNotifier, leaveBreadcrumb } from 'app/utils/error';
import { getLogger } from 'app/utils/logger';
import { settings } from 'app/utils/settingsStorage';
import { isDevel, query } from 'app/utils/varia';
import debug from 'debug';
const DefaultConfig = {
    pixelateUseBlur: false,
    imageCacheEnabled: false,
    multipleFilesSelectEnabled: true,
    fileUploadMaxSize: 10485760,
    desktopMainViewLayout: 'row',
    renumeratePinsAfterDeletion: false,
    pinNoteAnchor: true,
    autolockBlurs: true,
    shortcutsCopyToClipboard: 'meta+shift+c',
    // NOTE: revisit this after adding accounts
    watermarkDuringRollout: true,
    hideWatermarkDuringRollout: false,
};
const Delays = [5e3, 5e3, 5e3, 15e3, 3e4];
// -----------------------
// Features
// -----------------------
const features = {
    analytics: true,
    enableBackgrounds: true,
};
const hasFeature = (name) => features[name] === true;
// -----------------------
// Config
// -----------------------
let sharedConfig = {
    $id: nanoid(),
    value: Object.assign({}, DefaultConfig),
};
const reduceConfig = (config, value) => ({ $id: nanoid(), value: Object.assign(Object.assign({}, config.value), value) });
const configGet = (key, defaultValue) => {
    let value;
    const configValue = sharedConfig === null || sharedConfig === void 0 ? void 0 : sharedConfig.value[key];
    if (typeof configValue === 'undefined') {
        return defaultValue;
    }
    switch (typeof defaultValue) {
        case 'bigint':
        case 'number':
            value = Number.parseFloat(configValue.toString());
            if (Number.isNaN(value)) {
                value = undefined;
            }
            break;
        case 'boolean':
            value =
                ['1', 'true', 't', 'yes', 'y', 'on'].indexOf(configValue.toString()) !== -1;
            break;
        default:
            value = String(configValue);
            break;
    }
    return value !== null && value !== void 0 ? value : defaultValue;
};
const configSet = (key, value) => {
    const oldValue = sharedConfig.value[key];
    sharedConfig = reduceConfig(sharedConfig, { [key]: value });
    return oldValue;
};
export function getConfig(id) {
    return sharedConfig && id === sharedConfig.$id
        ? sharedConfig.value
        : undefined;
}
// -----------------------
// Initialize
// -----------------------
export function initGlobals() {
    const Global = global;
    Global.hasFeature = hasFeature;
    Global.configGet = configGet;
    Global.configSet = configSet;
    Global.getLogger = getLogger;
}
// -----------------------
// Action handlers
// -----------------------
function loadRemoteConfig(app) {
    return __awaiter(this, void 0, void 0, function* () {
        leaveBreadcrumb({}, '[RemoteConfig] Prepare');
        const idbAvailable = yield hasIndexedDB();
        if (!idbAvailable) {
            return null;
        }
        let remoteConfig;
        try {
            remoteConfig = getRemoteConfig(app);
        }
        catch (error) {
            remoteConfig = undefined;
            errorNotifier(error);
        }
        let cacheTTL = Number.parseInt(Env.config.CONFIG_FETCH_CACHE_TTL, 10);
        if (Number.isNaN(cacheTTL) ||
            !Number.isSafeInteger(cacheTTL) ||
            cacheTTL < 0) {
            cacheTTL = 1;
        }
        return Object.assign(Object.assign({}, remoteConfig), { settings: Object.assign(Object.assign({}, remoteConfig.settings), { minimumFetchIntervalMillis: cacheTTL }) });
    });
}
function loadFonts(fvds, urls, delay) {
    return __awaiter(this, void 0, void 0, function* () {
        return new Promise((resolve, reject) => {
            WebFont.load({
                timeout: Delays[delay],
                classes: !1,
                custom: {
                    families: fvds,
                    urls,
                    // testStrings: ((c = {}), (c[i] = face.loadTestString), c),
                },
                /*
                    fontloading: () => {
                    },
                    */
                fontactive: () => {
                    resolve();
                },
                fontinactive: () => {
                    loadFonts(fvds, urls, delay + 1).then(() => resolve(), () => reject(new Error('Could not load fonts')));
                },
            });
        });
    });
}
export const loadConfig = createAsyncThunk('editor/config', (_, { dispatch }) => __awaiter(void 0, void 0, void 0, function* () {
    // enable debug
    const isVerbose = query('verbose') === '1';
    if (isDevel || isVerbose) {
        debug.enable('*');
        configSet('verboseLog', true);
        configSet('imageCacheEnabled', true);
        getLogger('Config', 'info')('Verbose mode: ON');
    }
    else {
        debug.enable('app:error');
    }
    // NOTE: make this dynamic
    const firebaseConfig = {
        apiKey: 'AIzaSyC52lDxVrwVF_-Qf-Y7tSMqiwAAQ8mKgdo',
        authDomain: 'annotely-fd0a7.firebaseapp.com',
        projectId: 'annotely-fd0a7',
        storageBucket: 'annotely-fd0a7.appspot.com',
        messagingSenderId: '111404638877',
        appId: '1:111404638877:web:6eb62d24d887153734aa74',
        measurementId: 'G-4Z4XE25ZLV',
    };
    // Initialize Firebase
    const app = initializeApp(firebaseConfig);
    // fetch firebase remote config
    if (!isDevel) {
        const remoteConfig = yield loadRemoteConfig(app);
        if (remoteConfig) {
            // Load remote config
            try {
                yield fetchAndActivate(remoteConfig);
                // set remote config
                const values = getAll(remoteConfig);
                Object.entries(values).forEach(([key, value]) => {
                    if (value.getSource() === 'remote') {
                        sharedConfig.value[key] = value.asString();
                    }
                });
            }
            catch (err) {
                getLogger('Config', 'error')(err);
            }
        }
    }
    else {
        configSet('contactDiscordLink', 'https://discord.gg/fcbFDtsd');
        configSet('contactRedditLink', 'https://www.reddit.com/r/annotely/');
        configSet('contactTwitterLink', 'https://twitter.com/annotely');
        configSet('slidesListEnabled', true);
        configSet('showPagePreview', true);
    }
    // apply local changes
    Object.entries(settings().get()).forEach(([key, value]) => {
        sharedConfig.value[key] = value;
    });
    const Global = global;
    Global.configSet = (key, value) => {
        const oldValue = configSet(key, value);
        dispatch(EditorActions.setConfig({ version: sharedConfig.$id }));
        return oldValue;
    };
    dispatch(EditorActions.setConfig({ version: sharedConfig.$id }));
    // load fonts
    yield loadFonts(['drawn', 'code'], ['/fonts/editor-fonts.css'], 0);
    return Promise.resolve();
}));
